import { useEffect, useMemo, useState } from "react";
import { useParams, Navigate, Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { api, CITIES } from "@/lib/api";
import Seo from "@/components/Seo";
import FAQ from "@/components/FAQ";
import { Phone, Globe, Mail, MapPin } from "lucide-react";

export default function CityPage() {
    const { city } = useParams();
    const formatted = useMemo(() => {
        const c = decodeURIComponent(city || "").replace(/-/g, " ");
        return CITIES.find((x) => x.toLowerCase() === c.toLowerCase());
    }, [city]);

    const [clinics, setClinics] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        if (!formatted) return;
        api.get("/clinics", { params: { city: formatted } })
            .then((r) => setClinics(r.data))
            .finally(() => setLoading(false));
    }, [formatted]);

    if (!formatted) return <Navigate to="/clinics" replace />;

    const localFAQ = [
        { q: `Where can I find Aqualyx in ${formatted}?`, a: `Aqualyx is available at certified clinics in ${formatted}, listed below on this page. Only certified clinics offer the original Aqualyx in New Zealand.` },
        { q: `Is Aqualyx Medsafe registered in ${formatted}?`, a: `Yes. Aqualyx™ is a Class III medical device, Medsafe registered in New Zealand — including ${formatted}.` },
        { q: `How much does Aqualyx cost in ${formatted}?`, a: `Pricing varies per clinic and treatment area. Most clinics in ${formatted} offer a personalised consultation to outline the recommended course and total investment.` },
        { q: `How long does an Aqualyx appointment take in ${formatted}?`, a: `Most sessions take 30–45 minutes including pre-treatment preparation. Patients can typically return to work the same day.` },
    ];

    return (
        <div className="page-fade pt-32 md:pt-40 pb-24">
            <Seo
                title={`Aqualyx ${formatted} — Certified Fat Reduction Clinics | NZ`}
                description={`Find Aqualyx Certified Clinics in ${formatted}, New Zealand. Original injectable fat reduction — non-surgical body and double chin contouring. Medsafe registered. Book a consultation today.`}
                path={`/city/${city}`}
                breadcrumbs={[
                    { name: "Home", path: "/" },
                    { name: "Cities", path: "/clinics" },
                    { name: formatted, path: `/city/${city}` },
                ]}
                schema={{
                    "@context": "https://schema.org",
                    "@graph": [
                        {
                            "@type": "LocalBusiness",
                            "name": `Aqualyx ${formatted}`,
                            "areaServed": { "@type": "City", "name": formatted, "containedInPlace": { "@type": "Country", "name": "New Zealand" } },
                            "address": { "@type": "PostalAddress", "addressLocality": formatted, "addressCountry": "NZ" },
                            "url": `https://aqualyxnz.co.nz/city/${city}`
                        },
                        {
                            "@type": "ItemList",
                            "name": `Aqualyx Certified Clinics in ${formatted}`,
                            "numberOfItems": clinics.length,
                            "itemListElement": clinics.map((c, i) => ({
                                "@type": "ListItem",
                                "position": i + 1,
                                "item": {
                                    "@type": "MedicalClinic",
                                    "name": c.name,
                                    "address": c.address,
                                    "telephone": c.phone,
                                    "email": c.email,
                                    "url": c.website,
                                    "areaServed": formatted
                                }
                            }))
                        }
                    ]
                }}
            />
            <Helmet>
                <meta name="keywords" content={`Aqualyx ${formatted}, fat dissolving injections ${formatted}, double chin treatment ${formatted}, non surgical fat reduction ${formatted} NZ, body contouring ${formatted}`} />
            </Helmet>

            <section className="max-w-[1400px] mx-auto px-6 md:px-10">
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 mb-16">
                    <div className="lg:col-span-8 space-y-6">
                        <div className="eyebrow">{formatted}, New Zealand</div>
                        <h1 className="editorial-headline text-6xl md:text-8xl">
                            Aqualyx<br /><span className="italic text-champagne">{formatted}.</span>
                        </h1>
                    </div>
                    <div className="lg:col-span-4 flex items-end">
                        <p className="text-ink/70 leading-relaxed">
                            Original Aqualyx is available only through certified clinics in {formatted}. Browse providers below or book a consultation directly.
                        </p>
                    </div>
                </div>

                <div className="aspect-[16/7] w-full mb-12 border border-champagne/40">
                    <iframe
                        title={`Map of ${formatted}`}
                        src={`https://www.google.com/maps?q=${encodeURIComponent(formatted + ", New Zealand")}&output=embed`}
                        className="w-full h-full border-0"
                        loading="lazy"
                    />
                </div>

                <h2 className="font-serif text-4xl md:text-5xl mb-8">Certified Clinics in {formatted}</h2>

                {loading ? (
                    <div className="text-center py-20 eyebrow">Loading…</div>
                ) : clinics.length === 0 ? (
                    <div className="border border-champagne/40 p-10 text-center bg-nude">
                        <p className="font-serif italic text-2xl mb-4">We're expanding to {formatted}.</p>
                        <p className="text-ink/60 mb-6">Contact us to be notified when a certified clinic opens nearby.</p>
                        <Link to="/contact" className="btn-primary">Get Notified</Link>
                    </div>
                ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-px bg-champagne/30 border border-champagne/30 mb-16">
                        {clinics.map((c, i) => (
                            <article key={c.id} data-testid={`city-clinic-${i}`} className="bg-bone p-8 space-y-4">
                                <div className="eyebrow text-champagne">Certified · {formatted}</div>
                                <h3 className="font-serif text-3xl">{c.name}</h3>
                                <p className="text-sm text-ink/60 flex items-start gap-2"><MapPin size={14} strokeWidth={1.2} className="mt-1 shrink-0"/>{c.address}</p>
                                <div className="space-y-2 text-xs text-ink/70 pt-2 border-t border-champagne/20">
                                    {c.phone && <a href={`tel:${c.phone.replace(/\s+/g,"")}`} className="flex items-center gap-2 hover:text-champagne"><Phone size={12}/> {c.phone}</a>}
                                    {c.email && <a href={`mailto:${c.email}`} className="flex items-center gap-2 hover:text-champagne break-all"><Mail size={12}/> {c.email}</a>}
                                    {c.website && <a href={c.website} target="_blank" rel="noreferrer" className="flex items-center gap-2 hover:text-champagne"><Globe size={12}/> Website</a>}
                                </div>
                                <Link to={`/contact?clinic=${encodeURIComponent(c.name)}`} className="btn-primary text-[0.6rem] mt-3">Book Consultation</Link>
                            </article>
                        ))}
                    </div>
                )}

                <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 mt-20">
                    <div className="lg:col-span-5">
                        <div className="eyebrow mb-4">Local FAQ</div>
                        <h2 className="editorial-headline text-4xl md:text-6xl">
                            Aqualyx in<br /><span className="italic">{formatted}.</span>
                        </h2>
                    </div>
                    <div className="lg:col-span-7">
                        <FAQ items={localFAQ} />
                    </div>
                </div>
            </section>
        </div>
    );
}
